import { University } from "@/store/types/universityTypes";

interface RenderNodeProps {
  intake: string;
  date: string;
  applyBy: string;
}

const renderNode = ({ intake, date, applyBy }: RenderNodeProps) => (
  <div className="border border-[#D0C4FF] p-[24px] rounded-xl bg-white">
    <h4 className="text-2xl font-medium ff_poppins">{date}</h4>

    <p className="text-[#EB9757] text-base font-medium ff_poppins mt-1.5">
      Application Deadline: {applyBy}
    </p>
  </div>
);

const OpenIntakes = ({
  universityDetail,
}: {
  universityDetail: University;
}) => {
  const { intakeDetails } = universityDetail || {};

  const normalizeYear = (year: number): number => {
    if (!Number.isInteger(year)) return year;
    return year >= 0 && year <= 99 ? 2000 + year : year;
  };

  const createSafeDate = (
    year: number,
    month: number,
    day: number,
  ): Date | null => {
    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }
    return date;
  };

  const parseDateWithFallback = (dateStr: string): Date | null => {
    if (!dateStr || typeof dateStr !== "string") return null;

    const parts = dateStr.split("/").map((value) => parseInt(value.trim(), 10));
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
      return null;
    }

    const [firstPart, secondPart, rawYearPart] = parts;
    const yearPart = normalizeYear(rawYearPart);

    // Prefer DD/MM/YYYY; fallback to MM/DD/YYYY for inconsistent data.
    const preferredDate = createSafeDate(yearPart, secondPart, firstPart);
    if (preferredDate) {
      return preferredDate;
    }

    const fallbackDate = createSafeDate(yearPart, firstPart, secondPart);
    if (fallbackDate) {
      return fallbackDate;
    }

    return null;
  };

  const getFormattedMonthYear = (dateStr: string): string => {
    try {
      if (!dateStr.includes("/")) {
        throw new Error("Invalid format");
      }

      const dateParts = dateStr.split("/");

      if (dateParts.length === 2) {
        const [monthStr, yearStr] = dateParts;
        const monthNumber = parseInt(monthStr, 10);
        const yearNumber = normalizeYear(parseInt(yearStr, 10));

        if (
          isNaN(monthNumber) ||
          monthNumber < 1 ||
          monthNumber > 12 ||
          isNaN(yearNumber)
        ) {
          throw new Error("Invalid month");
        }

        const monthNames = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];

        return `${monthNames[monthNumber - 1]}, ${String(yearNumber).slice(-2)}`;
      }

      const parsedDate = parseDateWithFallback(dateStr);
      if (!parsedDate) {
        throw new Error("Unable to parse date");
      }

      const monthName = parsedDate.toLocaleString("en-US", { month: "long" });
      const shortYear = String(parsedDate.getFullYear()).slice(-2);

      return `${monthName}, 20${shortYear}`;
    } catch (error) {
      console.error("getFormattedMonthYear error:", (error as Error).message);
      return "Invalid date";
    }
  };

  const getOrdinalSuffix = (day: number): string => {
    if (day > 3 && day < 21) return "th";

    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const formatDateWithOrdinal = (dateStr: string): string => {
    try {
      const parsedDate = parseDateWithFallback(dateStr);

      if (!parsedDate || isNaN(parsedDate.getTime())) {
        throw new Error("Invalid date");
      }

      const day = parsedDate.getDate();
      const month = parsedDate.toLocaleString("en-US", {
        month: "long",
      });
      const year = parsedDate.getFullYear();

      return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
    } catch (error) {
      console.error("formatDateWithOrdinal error:", (error as Error).message);
      return "Invalid date";
    }
  };

  const uniqueOpenIntakes = Array.isArray(intakeDetails)
    ? Array.from(
        new Map(
          intakeDetails
            .flatMap((item, index) => {
              const {
                month = [],
                status = [],
                deadline = [],
              } = item?.intake_details || {};

              const openIndexes = status
                .map((s: string, i: number) =>
                  s?.toLowerCase() === "open" ? i : -1,
                )
                .filter((i: number) => i !== -1);

              return openIndexes.map((openIndex: number) => ({
                key: `${month[openIndex]}-${deadline[openIndex]}`,
                month: month[openIndex],
                deadline: deadline[openIndex],
                status: status[openIndex],
                originalIndex: index,
              }));
            })
            .map((item) => [item.key, item]),
        ).values(),
      ).sort((a, b) => {
        const parseMonthYearToDate = (monthStr: string) => {
          if (!monthStr) return new Date(0);
          const parts = monthStr.split("/");
          if (parts.length === 2) {
            const month = parseInt(parts[0], 10) || 1;
            const year = normalizeYear(parseInt(parts[1], 10)) || 0;
            return new Date(year, month - 1, 1);
          }
          return parseDateWithFallback(monthStr) || new Date(0);
        };

        const dateA = parseMonthYearToDate(a.month);
        const dateB = parseMonthYearToDate(b.month);

        if (dateA.getTime() !== dateB.getTime()) {
          return dateA.getTime() - dateB.getTime();
        }

        const deadlineA = parseDateWithFallback(a.deadline)?.getTime() || 0;
        const deadlineB = parseDateWithFallback(b.deadline)?.getTime() || 0;
        return deadlineA - deadlineB;
      })
    : [];

  return (
    <div className="pt-[32px] sm:pt-[40px] md:pt-[46px] lg:pt-[56px]">
      <p className="text-base ff_poppins font-medium text-primary pb-3">
        OPEN INTAKES
      </p>
      <div className="flex flex-col gap-5">
        {uniqueOpenIntakes?.map((item, index) => (
          <div key={index} className="w-full">
            {renderNode({
              intake: `${item.status} Intake - ${index + 1}`,
              date: getFormattedMonthYear(item.month) ?? "",
              applyBy: formatDateWithOrdinal(item.deadline),
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
export default OpenIntakes;
